import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, FileSpreadsheet, FileText, Share } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { ANALYTICS_TOOLBAR_BUTTON_COMPACT_CLASS } from "../shared/toolbarButtonStyles";
import {
  REVENUE_CHART_CARD_HEADER_GAP,
  REVENUE_CHART_CARD_HEADER_HEIGHT,
  REVENUE_CHART_CARD_PADDING,
  REVENUE_CHART_CARD_ICON_SIZE,
  REVENUE_CHART_CARD_ICON_STROKE_WIDTH,
  REVENUE_CHART_CARD_SHADOW,
  REVENUE_CHART_CARD_WIDTH,
} from "./constants";

interface RevenueChartCardProps {
  title: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  iconColor?: string;
  width?: "fixed" | "full";
  className?: string;
  style?: React.CSSProperties;
  cardWidth?: number;
  showExport?: boolean;
  iconSize?: number;
  onExport?: (format: "csv" | "xlsx") => void;
  exportDisabled?: boolean;
}

export const RevenueChartCard: React.FC<RevenueChartCardProps> = ({
  title,
  children,
  icon,
  iconColor = "#171717",
  width = "full",
  className,
  style,
  cardWidth,
  showExport = true,
  iconSize = REVENUE_CHART_CARD_ICON_SIZE,
  onExport,
  exportDisabled = false,
}) => {
  const { t } = useTranslation();
  const resolvedWidth =
    cardWidth ?? (width === "fixed" ? REVENUE_CHART_CARD_WIDTH : undefined);

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-2xl border border-[#E5E5E5] bg-white font-sans",
        width === "fixed" || cardWidth ? "max-w-full" : "w-full",
        REVENUE_CHART_CARD_SHADOW,
        className,
      )}
      style={{
        padding: REVENUE_CHART_CARD_PADDING,
        boxSizing: "border-box",
        ...(resolvedWidth ? { width: resolvedWidth, maxWidth: "100%" } : {}),
        ...style,
      }}
    >
      <div
        className="flex w-full items-center justify-between"
        style={{ minHeight: REVENUE_CHART_CARD_HEADER_HEIGHT }}
      >
        <div
          className="flex items-center"
          style={{ gap: REVENUE_CHART_CARD_HEADER_GAP }}
        >
          {icon ? (
            <span
              className="flex shrink-0 items-center justify-center"
              style={{
                color: iconColor,
                width: iconSize,
                height: iconSize,
              }}
            >
              {React.isValidElement(icon)
                ? React.cloneElement(
                    icon as React.ReactElement<{
                      className?: string;
                      strokeWidth?: number;
                      style?: React.CSSProperties;
                    }>,
                    {
                      className: "shrink-0",
                      style: { width: iconSize, height: iconSize },
                      strokeWidth: REVENUE_CHART_CARD_ICON_STROKE_WIDTH,
                    },
                  )
                : icon}
            </span>
          ) : null}
          <h3 className="shrink-0 text-sm font-semibold leading-normal text-[#171717]">
            {title}
          </h3>
        </div>
        {showExport ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={!onExport || exportDisabled}>
              <Button
                type="button"
                variant="span"
                className={ANALYTICS_TOOLBAR_BUTTON_COMPACT_CLASS}
              >
                <Share className="size-3.5 shrink-0" />
                {t("analytics.export.label", "Export")}
                <ChevronDown className="size-3.5 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onExport?.("xlsx")}>
                <FileSpreadsheet className="size-4" />
                {t("analytics.common.xlsx", "XLSX")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExport?.("csv")}>
                <FileText className="size-4" />
                {t("analytics.common.csv", "CSV")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
      {children}
    </div>
  );
};
